import React, { useEffect, useState } from "react";
import { getStoredItem } from "../../config/storageUtils";
import { useNavigate } from "react-router-dom";
import { nodalOfficersFormFields } from "../../forms/profileFields";
import {
  camelCaseToLabel,
  scriptRegex,
  validateField,
  validateSingleSectionFormData,
} from "../../utils/utils";

import DataFormField from "../common/DataFormField";
import Button from "../common/Button";
import api from "../../api/ApiService";
import Message from "../common/Message";

// NEW ROLE LIST IMPORTS
import {
  stateOfficerRoleList,
  districtOfficerRoleList,
} from "../../config/roleConfig";

const EditNodalOfficerForm = () => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [apiMessage, setApiMessage] = useState(null);

  const showDistrict = history.state?.usr?.showDistrict || false;

  useEffect(() => {
    const officerData = history.state?.usr?.officerData || {};
    setFormData(officerData);
  }, []);

  // ------------------ Dynamic Field Filtering + Role List -------------------
  const filteredFields = nodalOfficersFormFields.map((section) => ({
    ...section,
    fields: section.fields
      .map((field) => {
        // Role dropdown
        if (field.inputProps.name === "role_id") {
          return {
            ...field,
            inputProps: {
              ...field.inputProps,
              options: showDistrict
                ? districtOfficerRoleList
                : stateOfficerRoleList,
            },
          };
        }

        // Hide district field if not needed
        if (!showDistrict && field.inputProps.name === "district_name") {
          return null;
        }

        return field;
      })
      .filter(Boolean),
  }));

  // ------------------------------ On Change ------------------------------
  const handleChange = (e) => {
    const { name, type, files, value } = e.target;
    const newValue = type === "file" ? files[0] : value;

    if (!scriptRegex.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // ------------------------------ On Blur ------------------------------
  const handleBlur = (e) => {
    const { name, value } = e.target;

    let fieldConfig;

    nodalOfficersFormFields.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.inputProps.name === name) fieldConfig = field;
      });
    });

    if (!fieldConfig?.inputProps?.validation) return;

    // 🟡 Special case: email & mobile when showDistrict is false
    if (!showDistrict && (name === "email" || name === "mobile_number")) {
      // If empty → no error
      if (!value) {
        setErrors((prev) => ({ ...prev, [name]: null }));
        return;
      }

      // If value exists → validate ONLY pattern
      const { pattern, message } = fieldConfig.inputProps.validation;

      if (pattern && !pattern.test(value)) {
        setErrors((prev) => ({
          ...prev,
          [name]: message,
        }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: null }));
      }

      return;
    }

    // 🔵 Normal validation for all other cases
    const label = fieldConfig.label || camelCaseToLabel(name);
    const error = validateField(
      fieldConfig.inputProps.validation,
      value,
      label,
    );

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // ------------------------------ Submit ------------------------------
  const handleSubmit = async () => {
    const { errors: validationErrors } = validateSingleSectionFormData(
      filteredFields,
      formData,
    );

    // Clone so we don't mutate original object
    let updatedErrors = { ...validationErrors };

    // 🟡 Remove required errors when showDistrict is false
    if (!showDistrict) {
      if (updatedErrors?.email?.en.includes("required"))
        delete updatedErrors.email;
      if (updatedErrors?.mobile_number?.en.includes("required"))
        delete updatedErrors.mobile_number;
    }

    // 🔑 Recalculate isValid AFTER cleanup
    const isValid = Object.keys(updatedErrors).length === 0;

    setErrors(updatedErrors);

    if (!isValid) return;

    try {
      let resp;

      if (history.state?.usr?.officerData) {
        resp = await api.post("/admin/user/update", {
          id: history.state?.usr?.officerData.id,
          ...formData,
          status: "1",
          district_admin: showDistrict ? "1" : "0",
        });
      } else {
        resp = await api.post("/admin/user/create", {
          ...formData,
          district_admin: showDistrict ? "1" : "0",
        });
      }

      if (resp.status === 200 || resp.success === true) {
        navigate(
          showDistrict ? "/admin/district-officers" : "/admin/state-officers",
          {
            state: { message: "User saved successfully" },
          },
        );
      } else {
        let firstError =
          resp?.data &&
          typeof resp.data === "object" &&
          Object.values(resp?.data)[0]?.[0]
            ? Object.values(resp?.data)[0][0]
            : resp?.message || resp.data?.message;

        setApiMessage({
          type: "error",
          message: firstError || "Error submitting form. Please try again.",
        });
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        message: error.response?.data?.message || "Error saving user.",
      });
    }
  };

  // ------------------------------ JSX ------------------------------
  return (
    <>
      {apiMessage && (
        <Message apiMessage={apiMessage} setApiMessage={setApiMessage} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {filteredFields.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {section.fields?.map((field, fieldIndex) => (
              <DataFormField
                key={`${sectionIndex}-${fieldIndex}`}
                labelClass="block text-sm font-medium mb-1"
                divClass={field?.divClass}
                label={field?.label}
                htmlFor={field?.htmlFor}
                error={errors[field.inputProps.name]}
                inputClass="w-full border rounded px-3 py-2 text-sm"
                inputProps={{
                  required: field.inputProps.required || "false",
                  type: field.inputProps.type || "text",
                  name: field.inputProps.name,
                  id: field.inputProps.name,
                  placeholder: field.inputProps.placeholder || "",
                  disabled: field.inputProps.disabled || false,
                  value: formData[field.inputProps.name] || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                  options: field.inputProps.options || [],
                  maxLength: field.inputProps.maxLength || 255,
                }}
                formData={formData}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      <div className="flex justify-end mt-4">
        <Button className="bg-btn px-6 py-2 rounded" onClick={handleSubmit}>
          Submit
        </Button>
      </div>
    </>
  );
};

export default EditNodalOfficerForm;
